import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DonkeyGuide } from "@/components/DonkeyMascot";
import { ScoreRing } from "@/components/DonkeyBits";
import { toast } from "sonner";
import {
  Loader2, Rocket, Target, Megaphone, CalendarDays, Share2, Search, Mail, Users,
  BarChart3, Wand2, Building2, ListChecks, Gavel, Lock, Unlock, ArrowLeft, Plus, Trash2,
  CheckCircle2, AlertTriangle, RefreshCw, Copy,
} from "lucide-react";

const TABS = [
  ["overview", "Command Centre", Rocket],
  ["strategy", "Strategy", Target],
  ["message", "Message Map", Wand2],
  ["voice", "Brand Voice", Wand2],
  ["campaigns", "Campaigns", Megaphone],
  ["content", "Content", Share2],
  ["calendar", "Calendar", CalendarDays],
  ["social", "Publishing", Share2],
  ["seo", "SEO / AEO", Search],
  ["email", "Email", Mail],
  ["leads", "Leads", Users],
  ["analytics", "Analytics", BarChart3],
  ["ads", "Ads Planner", Megaphone],
  ["competitors", "Competitors", Building2],
  ["checklist", "Checklist", ListChecks],
  ["verdict", "Launch Verdict", Gavel],
];

const Card = ({ children, className = "", ...p }) => (
  <div className={`card-donkey rounded-2xl p-6 ${className}`} {...p}>{children}</div>
);
const Section = ({ title, children, action }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey">{title}</p>
      {action}
    </div>
    <div className="text-sm text-donkey-warm/90 leading-relaxed">{children}</div>
  </div>
);
const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {(items || []).map((x, i) => (
      <span key={i} className="text-xs rounded-full border border-white/15 bg-donkey-bg px-2.5 py-1 text-donkey-warm/85">{x}</span>
    ))}
  </div>
);

export default function LaunchWorkspace() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [gate, setGate] = useState(null);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const loadState = useCallback(async () => {
    const { data } = await api.get(`/launch/${bid}`);
    setState(data);
  }, [bid]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/launch/eligibility/${bid}`);
        setGate(data);
        if (data.can_enter && data.launch_started) await loadState();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load launch.");
      } finally { setLoading(false); }
    })();
  }, [bid, loadState]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post(`/launch/${bid}/start`);
      await loadState();
      setGate((g) => ({ ...g, launch_started: true }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setStarting(false); }
  };

  if (loading) return <div className="min-h-screen bg-donkey-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-donkey-orange" /></div>;

  if (!gate?.can_enter) {
    return (
      <div className="min-h-screen bg-donkey-bg grain">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
          <button onClick={() => navigate(`/build/${bid}`)} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5 mb-6" data-testid="launch-back-btn"><ArrowLeft className="w-4 h-4" /> Back to Build</button>
          <div className="flex items-center gap-2 mb-2"><Rocket className="w-6 h-6 text-donkey-orange" /><h1 className="font-heading font-extrabold text-3xl text-donkey-warm">Stage 4 — Launch</h1></div>
          <DonkeyGuide title="Not ready to launch yet" tone="warning" className="mt-6" data-testid="launch-gate-blocked">
            A business can enter Launch once it has a validated opportunity, a completed verdict and Stage 2 decision, a
            defined customer and problem, a credible offer and price, a Business Blueprint, a publishable website and a
            minimum-loss limit. Finish these in Build first.
          </DonkeyGuide>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {gate && Object.entries(gate.gate).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-sm card-donkey rounded-xl p-3" data-testid={`gate-${k}`}>
                {v ? <CheckCircle2 className="w-4 h-4 text-donkey-green" /> : <AlertTriangle className="w-4 h-4 text-donkey-amber" />}
                <span className="text-donkey-warm/85 capitalize">{k.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!gate.launch_started || !state) {
    return (
      <div className="min-h-screen bg-donkey-bg grain">
        <div className="mx-auto max-w-2xl px-5 sm:px-8 py-16 text-center">
          <Rocket className="w-12 h-12 text-donkey-orange mx-auto mb-6" />
          <h1 className="font-heading font-extrabold text-3xl text-donkey-warm">Launch {gate.name}</h1>
          <p className="mt-3 text-donkey-grey">Turn your built business into a controlled, evidence-led launch. The Donkey defaults to a small soft launch — proof before scale.</p>
          <Button onClick={start} disabled={starting} className="mt-8 bg-donkey-orange text-donkey-black font-semibold h-12 px-8" data-testid="start-launch-btn">
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start the launch <Rocket className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-donkey-bg grain text-donkey-warm overflow-x-hidden">
      <div className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <button onClick={() => navigate(`/build/${bid}`)} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5" data-testid="launch-back-btn"><ArrowLeft className="w-4 h-4" /> Build</button>
          <div className="flex items-center gap-2 min-w-0"><Rocket className="w-5 h-5 text-donkey-orange shrink-0" /><span className="font-heading font-semibold truncate">{state.name} · Launch</span></div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => navigate(`/grow/${bid}`)} className="text-xs rounded-full border border-white/15 text-donkey-warm hover:border-donkey-orange hover:text-donkey-orange px-3 py-1 transition-colors" data-testid="go-to-grow-btn">Grow →</button>
            <span className="text-xs rounded-full border border-donkey-orange/40 text-donkey-orange px-3 py-1 capitalize" data-testid="launch-phase-badge">{(state.phase || "").replace(/_/g, " ")}</span>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-3 sm:px-6 overflow-x-auto" role="region" aria-label="Launch sections" tabIndex={0}>
          <div className="flex gap-1 py-2 min-w-max" role="tablist" aria-label="Launch sections">
            {TABS.map(([id, label, Icon]) => (
              <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange/70 ${tab === id ? "bg-donkey-orange text-donkey-black font-semibold" : "text-donkey-grey hover:text-donkey-warm hover:bg-white/5"}`}
                data-testid={`launch-tab-${id}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8">
        {tab === "overview" && <Overview bid={bid} state={state} reload={loadState} />}
        {tab === "strategy" && <GenPanel bid={bid} kind="strategy" testid="strategy" title="Launch strategy" desc="An evidence-led plan: objective, customer, channel that fits, conversion action, budget ceiling, and pause/stop conditions." />}
        {tab === "message" && <GenPanel bid={bid} kind="message-map" testid="message" title="Customer & message map" desc="Who they are, the meaningful problem, honest promise and objections — no invented urgency or proof." />}
        {tab === "voice" && <BrandVoice bid={bid} state={state} reload={loadState} />}
        {tab === "campaigns" && <Campaigns bid={bid} />}
        {tab === "content" && <Content bid={bid} reload={loadState} />}
        {tab === "calendar" && <Calendar bid={bid} />}
        {tab === "social" && <Social bid={bid} />}
        {tab === "seo" && <Seo bid={bid} />}
        {tab === "email" && <Email bid={bid} />}
        {tab === "leads" && <Leads bid={bid} />}
        {tab === "analytics" && <Analytics bid={bid} />}
        {tab === "ads" && <Ads bid={bid} />}
        {tab === "competitors" && <Competitors bid={bid} />}
        {tab === "checklist" && <Checklist bid={bid} />}
        {tab === "verdict" && <Verdict bid={bid} />}
      </div>
    </div>
  );
}

// ---------------- Overview ----------------
function Overview({ state, reload }) {
  const r = state.readiness_score;
  return (
    <div className="space-y-6" data-testid="launch-overview">
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="text-center">
          <ScoreRing value={r} size={110} />
          <p className="text-sm text-donkey-grey mt-3">Launch readiness</p>
        </Card>
        <Card className="lg:col-span-2">
          <DonkeyGuide title="What the Donkey sees" tone={state.weak_evidence ? "caution" : "positive"}>
            {state.next_action}
            {state.weak_evidence && <p className="mt-2 text-donkey-amber">Demand evidence is still weak — treat this as a controlled test, not a validated business.</p>}
          </DonkeyGuide>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[["Leads", state.leads_total], ["Enquiries", state.conversions?.form_submit || 0], ["Scheduled", state.content_scheduled], ["Approvals due", state.content_pending_approval]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-donkey-bg border border-white/10 p-3 text-center" data-testid={`kpi-${l.toLowerCase().replace(/ /g, "-")}`}>
                <p className="font-mono text-2xl text-donkey-warm">{v}</p><p className="text-xs text-donkey-grey">{l}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <Section title="Target customer">{state.target_customer || "—"}</Section>
          <Section title="Primary offer">{state.primary_offer || "—"}</Section>
          <Section title="Primary objective">{state.primary_objective || "Set a strategy to define this."}</Section>
          <Section title="Channels"><Chips items={state.channels?.filter(Boolean)} /></Section>
        </Card>
        <Card>
          <Section title="Spend against limit">
            £{Number(state.spend_used || 0).toFixed(2)} {state.spend_limit ? `/ £${state.spend_limit}` : "· no budget ceiling set"}
          </Section>
          <Section title="Minimum-loss limit">{state.minimum_loss_limit || "—"}</Section>
          <Section title="Blockers">
            {state.blockers?.length ? <Chips items={state.blockers.map((b) => b.label)} /> : <span className="text-donkey-green">None — soft launch is possible.</span>}
          </Section>
          <Section title="Optional improvements"><Chips items={(state.improvements || []).map((b) => b.label)} /></Section>
        </Card>
      </div>
      <Button variant="ghost" onClick={reload} className="text-donkey-grey border border-white/10" data-testid="overview-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
    </div>
  );
}

// ---------------- Generic generate/display panel (strategy, message-map) ----------------
function GenPanel({ bid, kind, testid, title, desc }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/launch/${bid}`); setData((kind === "strategy" ? data.has_strategy : data.has_message_map) ? "loaded" : null); } catch {} })(); }, [bid, kind]);
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/launch/${bid}/${kind}/generate`); setData(data); toast.success(`${title} ready`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const load = async () => { const { data } = await api.get(`/launch/${bid}`); /* strategy/message stored on launch doc; fetch via command centre not enough */ };
  return (
    <Card data-testid={`panel-${testid}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div><h2 className="font-heading font-semibold text-lg">{title}</h2><p className="text-sm text-donkey-grey mt-1 max-w-2xl">{desc}</p></div>
        <Button onClick={gen} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid={`gen-${testid}-btn`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-2" /> {data ? "Regenerate" : "Generate"}</>}
        </Button>
      </div>
      {data && typeof data === "object" && (
        <div className="mt-6 space-y-3" data-testid={`${testid}-result`}>
          {Object.entries(data).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-donkey-bg border border-white/10 p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange mb-1.5">{k.replace(/_/g, " ")}</p>
              {Array.isArray(v) ? (
                <ul className="space-y-1.5">{v.map((it, i) => <li key={i} className="text-sm text-donkey-warm/85">{typeof it === "object" ? Object.values(it).join(" — ") : it}</li>)}</ul>
              ) : typeof v === "object" ? <pre className="text-xs text-donkey-grey whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>
                : <p className="text-sm text-donkey-warm/85">{String(v)}</p>}
            </div>
          ))}
        </div>
      )}
      {data === "loaded" && <p className="mt-6 text-sm text-donkey-grey">Already generated — click Regenerate to refresh, or view it in the Command Centre.</p>}
    </Card>
  );
}

// ---------------- Brand voice (with lock) ----------------
function BrandVoice({ bid, state, reload }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const locked = state.brand_voice_locked;
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/launch/${bid}/brand-voice/generate`); setData(data); toast.success("Brand voice ready"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggleLock = async () => {
    try { await api.post(`/launch/${bid}/brand-voice/lock`, { locked: !locked }); await reload(); toast.success(!locked ? "Brand voice locked" : "Unlocked"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card data-testid="panel-voice">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div><h2 className="font-heading font-semibold text-lg">Brand voice</h2><p className="text-sm text-donkey-grey mt-1 max-w-2xl">All generated content uses this saved voice. Lock it to keep it consistent.</p></div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={toggleLock} className="border border-white/10 text-donkey-warm" data-testid="lock-voice-btn">
            {locked ? <><Lock className="w-4 h-4 mr-2 text-donkey-orange" /> Locked</> : <><Unlock className="w-4 h-4 mr-2" /> Unlocked</>}
          </Button>
          <Button onClick={gen} disabled={busy || locked} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="gen-voice-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-2" /> Generate</>}
          </Button>
        </div>
      </div>
      {data && (
        <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="voice-result">
          {Object.entries(data).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-donkey-bg border border-white/10 p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange mb-1.5">{k.replace(/_/g, " ")}</p>
              {Array.isArray(v) ? <Chips items={v.map((x) => typeof x === "object" ? Object.values(x).join(": ") : x)} /> : <p className="text-sm text-donkey-warm/85">{String(v)}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------- Campaigns ----------------
function Campaigns({ bid }) {
  const [items, setItems] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [form, setForm] = useState({ name: "", objective: "lead_generation", channel: "linkedin", offer: "", audience: "", message_angle: "", budget: 0, conversion_goal: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/launch/${bid}/campaigns`); setItems(data.campaigns); setObjectives(data.objectives); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.name.trim()) return toast.error("Name the campaign");
    setBusy(true);
    try { await api.post(`/launch/${bid}/campaigns`, form); setForm({ ...form, name: "", offer: "", audience: "", message_angle: "" }); await load(); toast.success("Campaign created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setStatus = async (c, status) => { await api.put(`/launch/${bid}/campaigns/${c.id}`, { status }); await load(); };
  const del = async (c) => { await api.delete(`/launch/${bid}/campaigns/${c.id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="panel-campaigns">
      <Card>
        <h2 className="font-heading font-semibold text-lg mb-3">New campaign</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Campaign name" className="bg-donkey-bg border-white/15" data-testid="campaign-name-input" />
          <select value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm" aria-label="Campaign objective" data-testid="campaign-objective-select">
            {objectives.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
          </select>
          <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm" aria-label="Campaign channel" data-testid="campaign-channel-select">
            {["linkedin", "facebook", "instagram", "tiktok", "youtube", "x", "email", "local"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input value={form.conversion_goal} onChange={(e) => setForm({ ...form, conversion_goal: e.target.value })} placeholder="Conversion goal" className="bg-donkey-bg border-white/15" data-testid="campaign-goal-input" />
          <Input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder="Audience" className="bg-donkey-bg border-white/15" />
          <Input value={form.offer} onChange={(e) => setForm({ ...form, offer: e.target.value })} placeholder="Offer" className="bg-donkey-bg border-white/15" />
        </div>
        <Button onClick={create} disabled={busy} className="mt-3 bg-donkey-orange text-donkey-black font-semibold" data-testid="create-campaign-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Create campaign</>}</Button>
      </Card>
      <div className="space-y-2" data-testid="campaign-list">
        {items.length === 0 && <p className="text-sm text-donkey-grey">No campaigns yet.</p>}
        {items.map((c) => (
          <Card key={c.id} className="!p-4" data-testid={`campaign-${c.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div><p className="font-medium">{c.name}</p><p className="text-xs text-donkey-grey">{(c.objective || "").replace(/_/g, " ")} · {c.channel} · goal: {c.conversion_goal || "—"}</p></div>
              <div className="flex items-center gap-2">
                <select value={c.status} onChange={(e) => setStatus(c, e.target.value)} className="bg-donkey-bg border border-white/15 rounded-lg px-2 py-1 text-xs" aria-label="Campaign status" data-testid={`campaign-status-${c.id}`}>
                  {["draft", "ready", "active", "live", "paused", "complete", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => del(c)} className="text-donkey-grey hover:text-donkey-red" aria-label="Delete campaign" data-testid={`delete-campaign-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- Content ----------------
function Content({ bid }) {
  const [items, setItems] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [sel, setSel] = useState(["linkedin"]);
  const [type, setType] = useState("educational");
  const [count, setCount] = useState(2);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/launch/${bid}/content`); setItems(data.content); setPlatforms(data.platforms); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const gen = async () => {
    setBusy(true);
    try { await api.post(`/launch/${bid}/content/generate`, { platforms: sel, content_type: type, count: Number(count) }); await load(); toast.success("Content drafted — review and approve before scheduling."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setStatus = async (it, status) => { try { await api.put(`/launch/${bid}/content/${it.id}`, { status }); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const publish = async (it) => { try { const { data } = await api.post(`/launch/${bid}/content/${it.id}/publish`); await load(); toast.success(data.note); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (it) => { await api.delete(`/launch/${bid}/content/${it.id}`); await load(); };
  const toggle = (p) => setSel((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);
  return (
    <div className="space-y-4" data-testid="panel-content">
      <Card>
        <h2 className="font-heading font-semibold text-lg mb-3">Generate social content</h2>
        <p className="text-xs text-donkey-grey mb-3">Platform-appropriate versions in your brand voice. Everything is a draft until you approve it — nothing publishes automatically.</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {platforms.map((p) => <button key={p} onClick={() => toggle(p)} className={`text-xs rounded-full border px-3 py-1 ${sel.includes(p) ? "border-donkey-orange bg-donkey-orange/10 text-donkey-warm" : "border-white/15 text-donkey-grey"}`} data-testid={`platform-${p}`}>{p}</button>)}
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <select value={type} onChange={(e) => setType(e.target.value)} className="bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm" aria-label="Content type" data-testid="content-type-select">
            {["educational", "problem_awareness", "founder_led", "promotional", "launch_announcement", "follow_up"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <Input type="number" min={1} max={8} value={count} onChange={(e) => setCount(e.target.value)} className="w-20 bg-donkey-bg border-white/15" aria-label="Number of content items" data-testid="content-count-input" />
          <Button onClick={gen} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="gen-content-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-2" /> Generate</>}</Button>
        </div>
      </Card>
      <div className="space-y-2" data-testid="content-list">
        {items.length === 0 && <p className="text-sm text-donkey-grey">No content yet.</p>}
        {items.map((it) => (
          <Card key={it.id} className="!p-4" data-testid={`content-${it.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
              <div className="flex items-center gap-2 text-xs"><span className="rounded-full border border-white/15 px-2 py-0.5 capitalize">{it.platform}</span><span className="text-donkey-grey">{(it.content_type || "").replace(/_/g, " ")}</span><span className="text-donkey-orange">{it.funnel_stage}</span></div>
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${it.status === "approved" ? "text-donkey-green border-donkey-green/40" : it.status === "published" ? "text-donkey-orange border-donkey-orange/40" : "text-donkey-grey border-white/20"}`} data-testid={`content-status-${it.id}`}>{it.status.replace(/_/g, " ")}</span>
            </div>
            {it.title && <p className="font-medium text-sm">{it.title}</p>}
            <p className="text-sm text-donkey-warm/80 whitespace-pre-wrap mt-1">{it.body}</p>
            {it.call_to_action && <p className="text-xs text-donkey-orange mt-1">CTA: {it.call_to_action}</p>}
            <p className="text-[11px] text-donkey-grey mt-1">Purpose: {it.purpose} · Watch: {it.metric_to_monitor}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {["draft", "ready_for_review", "needs_evidence"].includes(it.status) && <Button size="sm" onClick={() => setStatus(it, "approved")} className="bg-donkey-green/20 text-donkey-green border border-donkey-green/40 h-8" data-testid={`approve-${it.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve</Button>}
              {["approved"].includes(it.status) && <Button size="sm" onClick={() => setStatus(it, "scheduled")} className="bg-donkey-orange/20 text-donkey-orange border border-donkey-orange/40 h-8" data-testid={`schedule-${it.id}`}>Schedule</Button>}
              {["approved", "scheduled"].includes(it.status) && <Button size="sm" onClick={() => publish(it)} className="bg-white/5 text-donkey-warm border border-white/15 h-8" data-testid={`publish-${it.id}`}><Copy className="w-3.5 h-3.5 mr-1.5" /> Mark published (manual)</Button>}
              <button onClick={() => del(it)} className="text-donkey-grey hover:text-donkey-red ml-auto" aria-label="Delete content" data-testid={`delete-content-${it.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- Calendar ----------------
function Calendar({ bid }) {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get(`/launch/${bid}/calendar`); setItems(data.items); })(); }, [bid]);
  const groups = { scheduled: [], approved: [], draft: [], published: [], other: [] };
  items.forEach((i) => (groups[i.status] || groups.other).push(i));
  return (
    <div className="space-y-4" data-testid="panel-calendar">
      <Card>
        <h2 className="font-heading font-semibold text-lg mb-1">Content calendar</h2>
        <p className="text-xs text-donkey-grey">Unreviewed AI content never publishes automatically. Approve content in the Content tab, then schedule it.</p>
      </Card>
      {Object.entries(groups).map(([g, arr]) => arr.length > 0 && (
        <Card key={g} data-testid={`calendar-group-${g}`}>
          <p className="text-xs uppercase tracking-[0.18em] text-donkey-orange mb-3 capitalize">{g}</p>
          <div className="space-y-2">
            {arr.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 rounded-lg bg-donkey-bg border border-white/10 p-3 text-sm" data-testid={`calendar-item-${i.id}`}>
                <div className="min-w-0"><span className="text-xs rounded-full border border-white/15 px-2 py-0.5 capitalize mr-2">{i.platform}</span><span className="text-donkey-warm/85">{i.title || i.content_type}</span></div>
                <span className="text-xs text-donkey-grey shrink-0">{i.scheduled_at ? new Date(i.scheduled_at).toLocaleString() : "unscheduled"}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}
      {items.length === 0 && <p className="text-sm text-donkey-grey">No content scheduled yet.</p>}
    </div>
  );
}

// ---------------- Social publishing (connectors) ----------------
function Social({ bid }) {
  const [c, setC] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get(`/launch/${bid}/connectors`); setC(data.connectors); })(); }, [bid]);
  return (
    <div className="space-y-4" data-testid="panel-social">
      <DonkeyGuide title="Publishing is manual for now" tone="caution">
        No social accounts are connected and no content posts automatically. Approve content, then copy or download it and post it yourself. Scheduled content is preserved for when a connection is added later.
      </DonkeyGuide>
      <div className="grid sm:grid-cols-2 gap-3">
        {c.map((x) => (
          <Card key={x.platform} className="!p-4" data-testid={`connector-${x.platform}`}>
            <div className="flex items-center justify-between"><p className="font-medium capitalize">{x.platform}</p><span className="text-[10px] uppercase text-donkey-amber border border-donkey-amber/40 rounded-full px-2 py-0.5">{x.status}</span></div>
            <p className="text-xs text-donkey-grey mt-2">{x.setup_guidance}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- SEO / AEO ----------------
function Seo({ bid }) {
  const [items, setItems] = useState([]);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/launch/${bid}/seo`); setItems(data.articles); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const gen = async () => { setBusy(true); try { await api.post(`/launch/${bid}/seo/generate`, { topic }); await load(); toast.success("SEO pack generated — now write the article."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const writeArticle = async (a) => { try { await api.post(`/launch/${bid}/seo/${a.id}/article`); await load(); toast.success("Article written — check the quality gate."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const publish = async (a) => { try { const { data } = await api.post(`/launch/${bid}/seo/${a.id}/publish`, { confirm_overwrite: true }); await load(); toast.success("Published to website · sitemap updated"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-4" data-testid="panel-seo">
      <Card>
        <h2 className="font-heading font-semibold text-lg mb-3">SEO / AEO content engine</h2>
        <div className="flex gap-3"><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic focus (optional)" className="bg-donkey-bg border-white/15" data-testid="seo-topic-input" /><Button onClick={gen} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="gen-seo-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate pack"}</Button></div>
      </Card>
      {items.map((a) => (
        <Card key={a.id} data-testid={`article-${a.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-medium">{a.article?.title || a.recommended_article_topic || "SEO pack"}</p>
            {a.quality_gate && <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${a.quality_gate?.can_publish ? "text-donkey-green border-donkey-green/40" : "text-donkey-red border-donkey-red/40"}`} data-testid={`gate-${a.id}`}>gate {a.quality_gate?.passed}/{a.quality_gate?.total}</span>}
          </div>
          {a.keyword_map?.length > 0 && <p className="text-xs text-donkey-grey mt-1">{a.keyword_map.length} keywords · {a.topic_clusters?.length || 0} clusters</p>}
          {a.article?.meta_description && <p className="text-xs text-donkey-grey mt-1">{a.article.meta_description}</p>}
          {a.quality_gate && !a.quality_gate?.can_publish && a.quality_gate?.blocked_reason && <p className="text-xs text-donkey-red mt-1">{a.quality_gate.blocked_reason}</p>}
          <div className="flex gap-2 mt-3">
            {!a.article && <Button size="sm" onClick={() => writeArticle(a)} className="bg-white/5 border border-white/15 text-donkey-warm h-8" data-testid={`write-article-${a.id}`}><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Write article</Button>}
            {a.article && <Button size="sm" disabled={!a.quality_gate?.can_publish || a.status === "published"} onClick={() => publish(a)} className="bg-donkey-orange text-donkey-black h-8" data-testid={`publish-article-${a.id}`}>{a.status === "published" ? "Published" : "Publish to website"}</Button>}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------- Email ----------------
function Email({ bid }) {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("welcome");
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/launch/${bid}/email`); setItems(data.sequences); setTypes(data.types); setStatus(data.sending_status); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const gen = async () => { setBusy(true); try { await api.post(`/launch/${bid}/email/generate`, { sequence_type: type }); await load(); toast.success("Sequence drafted"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const del = async (e) => { await api.delete(`/launch/${bid}/email/${e.id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="panel-email">
      <DonkeyGuide title="Sending is off" tone="caution">{status}</DonkeyGuide>
      <Card>
        <div className="flex gap-3"><select value={type} onChange={(e) => setType(e.target.value)} className="bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm" aria-label="Email sequence type" data-testid="email-type-select">{types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select><Button onClick={gen} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="gen-email-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Draft sequence"}</Button></div>
      </Card>
      {items.map((s) => (
        <Card key={s.id} data-testid={`email-${s.id}`}>
          <div className="flex items-center justify-between"><p className="font-medium">{s.sequence_name}</p><button onClick={() => del(s)} className="text-donkey-grey hover:text-donkey-red" aria-label="Delete email sequence"><Trash2 className="w-4 h-4" /></button></div>
          <div className="mt-3 space-y-2">
            {(s.emails || []).map((em, i) => (
              <div key={i} className="rounded-lg bg-donkey-bg border border-white/10 p-3">
                <p className="text-sm font-medium">{em.subject}</p>
                <p className="text-xs text-donkey-grey">{em.preview_text} · {em.delay}</p>
                <p className="text-sm text-donkey-warm/80 whitespace-pre-wrap mt-1">{em.body}</p>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---------------- Leads ----------------
function Leads({ bid }) {
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const load = useCallback(async () => { const { data } = await api.get(`/launch/${bid}/leads`); setItems(data.leads); setStatuses(data.statuses); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const upd = async (l, patch) => { await api.put(`/launch/${bid}/leads/${l.id}`, patch); await load(); };
  return (
    <div className="space-y-2" data-testid="panel-leads">
      <Card><h2 className="font-heading font-semibold text-lg">Leads</h2><p className="text-xs text-donkey-grey mt-1">Leads captured from your published Stage 3 website.</p></Card>
      {items.length === 0 && <p className="text-sm text-donkey-grey">No leads yet.</p>}
      {items.map((l) => (
        <Card key={l.id} className="!p-4" data-testid={`lead-${l.id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0"><p className="font-medium text-sm">{l.name || l.email || "Enquiry"}</p><p className="text-xs text-donkey-grey">{l.email} {l.phone ? "· " + l.phone : ""} · {l.source_page}</p>{l.message && <p className="text-sm text-donkey-warm/80 mt-1">{l.message}</p>}</div>
            <select value={l.status} onChange={(e) => upd(l, { status: e.target.value })} className="bg-donkey-bg border border-white/15 rounded-lg px-2 py-1 text-xs" aria-label="Lead status" data-testid={`lead-status-${l.id}`}>
              {statuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <Input defaultValue={l.next_action || ""} onBlur={(e) => e.target.value !== (l.next_action || "") && upd(l, { next_action: e.target.value })} placeholder="Next action…" className="mt-2 bg-donkey-bg border-white/15 h-8 text-xs" data-testid={`lead-next-${l.id}`} />
        </Card>
      ))}
    </div>
  );
}

// ---------------- Analytics ----------------
function Analytics({ bid }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get(`/launch/${bid}/performance`); setData(data); })(); }, [bid]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  const tone = { first_party: "text-donkey-green", user_entered: "text-donkey-amber", verified: "text-donkey-green", inference: "text-donkey-orange", unavailable: "text-donkey-grey" };
  return (
    <div className="space-y-4" data-testid="panel-analytics">
      <Card>
        <h2 className="font-heading font-semibold text-lg mb-3">Performance</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(data.metrics).map(([k, m]) => (
            <div key={k} className="rounded-xl bg-donkey-bg border border-white/10 p-3" data-testid={`metric-${k}`}>
              <p className="font-mono text-xl text-donkey-warm">{m.value === null ? "—" : m.value}</p>
              <p className="text-xs text-donkey-grey capitalize">{k.replace(/_/g, " ")}</p>
              <p className={`text-[10px] mt-1 ${tone[m.source] || "text-donkey-grey"}`}>{data.legend[m.source]}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-donkey-grey mt-4">Nothing is fabricated. Unavailable metrics need a connected platform or verified payment provider.</p>
      </Card>
    </div>
  );
}

// ---------------- Ads ----------------
function Ads({ bid }) {
  const [safety, setSafety] = useState(null);
  const [platform, setPlatform] = useState("meta");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => { const { data } = await api.get(`/launch/${bid}/ads/safety`); setSafety(data); })(); }, [bid]);
  const gen = async () => { setBusy(true); try { const { data } = await api.post(`/launch/${bid}/ads/plan`, { platform, confirm: false }); setPlan(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  return (
    <div className="space-y-4" data-testid="panel-ads">
      <DonkeyGuide title="Planning only — no money is spent" tone="caution">
        No ad account is connected and no spend happens here. The Donkey will not plan paid ads until the safety checks pass — never spend to prove commitment.
      </DonkeyGuide>
      {safety && (
        <Card>
          <div className="grid sm:grid-cols-2 gap-2">
            {Object.entries(safety.checks).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-sm" data-testid={`ad-check-${k}`}>{v ? <CheckCircle2 className="w-4 h-4 text-donkey-green" /> : <AlertTriangle className="w-4 h-4 text-donkey-amber" />}<span className="capitalize text-donkey-warm/85">{k.replace(/_/g, " ")}</span></div>
            ))}
          </div>
          <div className="flex gap-3 mt-4 items-center">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm" aria-label="Advertising platform" data-testid="ad-platform-select">{safety.platforms.map((p) => <option key={p} value={p}>{p}</option>)}</select>
            <Button onClick={gen} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="gen-ad-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Plan campaign"}</Button>
          </div>
          {!safety.safe_to_plan_paid && <p className="text-xs text-donkey-amber mt-2">Resolve the checks above before planning paid ads.</p>}
        </Card>
      )}
      {plan && (
        <Card data-testid="ad-plan-result">
          {Object.entries(plan).filter(([k]) => !["safety"].includes(k)).map(([k, v]) => (
            <div key={k} className="mb-2"><span className="text-xs uppercase tracking-[0.15em] text-donkey-orange">{k.replace(/_/g, " ")}: </span><span className="text-sm text-donkey-warm/85">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ---------------- Competitors ----------------
function Competitors({ bid }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", offer: "", pricing: "", positioning: "", url: "" });
  const [q, setQ] = useState("");
  const [research, setResearch] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get(`/launch/${bid}/competitors`); setItems(data.competitors); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!form.name.trim()) return; await api.post(`/launch/${bid}/competitors`, form); setForm({ name: "", offer: "", pricing: "", positioning: "", url: "" }); await load(); };
  const del = async (c) => { await api.delete(`/launch/${bid}/competitors/${c.id}`); await load(); };
  const doResearch = async () => { if (!q.trim()) return; setBusy(true); try { const { data } = await api.post(`/launch/${bid}/competitors/research`, { query: q }); setResearch(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  return (
    <div className="space-y-4" data-testid="panel-competitors">
      <Card>
        <h2 className="font-heading font-semibold text-lg mb-3">Record a competitor</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="bg-donkey-bg border-white/15" data-testid="competitor-name-input" />
          <Input value={form.pricing} onChange={(e) => setForm({ ...form, pricing: e.target.value })} placeholder="Pricing" className="bg-donkey-bg border-white/15" />
          <Input value={form.offer} onChange={(e) => setForm({ ...form, offer: e.target.value })} placeholder="Offer" className="bg-donkey-bg border-white/15" />
          <Input value={form.positioning} onChange={(e) => setForm({ ...form, positioning: e.target.value })} placeholder="Positioning" className="bg-donkey-bg border-white/15" />
        </div>
        <Button onClick={add} className="mt-3 bg-donkey-orange text-donkey-black font-semibold" data-testid="add-competitor-btn"><Plus className="w-4 h-4 mr-2" /> Add</Button>
      </Card>
      <Card>
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Research the market (cited)</p>
        <div className="flex gap-3"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. phone repair pricing Bristol" className="bg-donkey-bg border-white/15" data-testid="competitor-research-input" /><Button onClick={doResearch} disabled={busy} className="bg-white/5 border border-white/15 text-donkey-warm" data-testid="research-competitors-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Research</>}</Button></div>
        {research && <div className="mt-3 space-y-1.5" data-testid="research-results">{(research.sources || []).map((s, i) => <a key={i} href={s.url} target="_blank" rel="noreferrer" className="block text-xs text-donkey-orange hover:underline">{s.title} <span className="text-donkey-grey">({s.authority})</span></a>)}<p className="text-[11px] text-donkey-grey mt-2">{research.note}</p></div>}
      </Card>
      <div className="space-y-2">
        {items.map((c) => (
          <Card key={c.id} className="!p-4" data-testid={`competitor-${c.id}`}>
            <div className="flex items-center justify-between"><p className="font-medium">{c.name}</p><button onClick={() => del(c)} className="text-donkey-grey hover:text-donkey-red" aria-label="Delete competitor"><Trash2 className="w-4 h-4" /></button></div>
            <p className="text-xs text-donkey-grey mt-1">{c.pricing} · {c.positioning}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------- Checklist ----------------
function Checklist({ bid }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get(`/launch/${bid}/checklist`); setData(data); })(); }, [bid]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  return (
    <Card data-testid="panel-checklist">
      <div className="flex items-center justify-between mb-4"><h2 className="font-heading font-semibold text-lg">Launch checklist</h2><span className="font-mono text-donkey-orange">{data.done}/{data.total}</span></div>
      <div className="grid sm:grid-cols-2 gap-2">
        {Object.entries(data.items).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-sm rounded-lg bg-donkey-bg border border-white/10 p-3" data-testid={`checklist-${k}`}>
            {v ? <CheckCircle2 className="w-4 h-4 text-donkey-green" /> : <AlertTriangle className="w-4 h-4 text-donkey-amber" />}
            <span className="text-donkey-warm/85 capitalize">{k.replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------- Verdict ----------------
function Verdict({ bid }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async () => { setBusy(true); try { const { data } = await api.post(`/launch/${bid}/verdict`); setData(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const tone = { "STOP": "text-donkey-red border-donkey-red/50 bg-donkey-red/10", "NOT READY": "text-donkey-amber border-donkey-amber/50 bg-donkey-amber/10", "PAUSE AND FIX": "text-donkey-amber border-donkey-amber/50 bg-donkey-amber/10" };
  return (
    <Card data-testid="panel-verdict">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div><h2 className="font-heading font-semibold text-lg">Donkey Launch Verdict</h2><p className="text-sm text-donkey-grey mt-1">An honest go/no-go, defaulting to a controlled test unless evidence supports more.</p></div>
        <Button onClick={gen} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="gen-verdict-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Gavel className="w-4 h-4 mr-2" /> Get the verdict</>}</Button>
      </div>
      {data && (
        <div className="mt-6" data-testid="verdict-result">
          <span className={`inline-block text-sm font-bold uppercase px-3 py-1.5 rounded-lg border ${tone[data.verdict] || "text-donkey-green border-donkey-green/50 bg-donkey-green/10"}`} data-testid="verdict-label">{data.verdict}</span>
          <p className="mt-3 text-donkey-warm/90 leading-relaxed">{data.explanation}</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {Object.entries(data.answers || {}).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-donkey-bg border border-white/10 p-3"><p className="text-[11px] uppercase tracking-wide text-donkey-grey">{k.replace(/_/g, " ")}</p><p className="text-sm text-donkey-warm/85">{v}</p></div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
